import React, { useCallback, useEffect, useState } from 'react';
import { MapPin, ExternalLink, RotateCcw, Pencil } from 'lucide-react';
import { customerLocationApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast/Toast';
import VisitLocationModal from './VisitLocationModal';

/**
 * A customer's two positions, side by side, with a way to fix either.
 *
 * They are not interchangeable and mixing them up has already caused real
 * confusion ("no location, so how is this 370 m away?"):
 *
 *   Check-in position — written by GPS when someone checked in on site. It is
 *     the evidence a visit happened, and the 200m check-in rule is measured
 *     from it. Reports fall back to it when there is no map pin.
 *   Map pin — typed in from a Google Maps link (or derived from the address).
 *     It only sorts routes; it never authorises a check-in.
 *
 * Resetting is admin-only because clearing the check-in anchor stops the team
 * checking in at that customer until a fresh visit records one.
 */

interface Props {
  customerId: number;
  customerName: string;
}

type Point = { lat: number; lng: number; source?: string; precision?: string; updated_at?: string } | null;

const SOURCE_NOTE: Record<string, string> = {
  manual: 'set by hand from a map link',
  google: 'looked up from the address',
  nominatim: 'looked up from the address',
  pincode: 'pincode area only',
};

const CustomerLocationCard: React.FC<Props> = ({ customerId, customerName }) => {
  const { isAdmin } = useAuth();
  const { showSuccess, showError } = useToast();
  const [checkin, setCheckin] = useState<Point>(null);
  const [map, setMap] = useState<Point>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await customerLocationApi.get(customerId);
      setCheckin(d.checkin as Point);
      setMap(d.map as Point);
    } catch (e: any) {
      showError('Could not load location', e?.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const reset = async (target: 'map' | 'checkin') => {
    const what = target === 'checkin'
      ? `Clear the check-in GPS position for ${customerName}?\n\nNobody will be able to check in there until a new visit records a position on site.`
      : `Clear the map pin for ${customerName}?\n\nThe customer drops to the end of the route until a new pin is set.`;
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(what)) return;
    setBusy(true);
    try {
      const d = await customerLocationApi.reset(customerId, target);
      setCheckin(d.checkin as Point);
      setMap(d.map as Point);
      showSuccess('Location reset', target === 'checkin' ? 'The check-in position has been cleared' : 'The map pin has been cleared');
    } catch (e: any) {
      showError('Could not reset', e?.message || 'Unknown error');
    } finally {
      setBusy(false);
    }
  };

  const Coordinates: React.FC<{ point: Point }> = ({ point }) => (
    point ? (
      <a
        href={`https://www.google.com/maps?q=${point.lat},${point.lng}`}
        target="_blank" rel="noopener noreferrer"
        className="font-mono text-[12px] text-blue-600 hover:underline inline-flex items-center gap-1"
        title="Open this position in Google Maps"
      >
        {point.lat.toFixed(6)}, {point.lng.toFixed(6)}
        <ExternalLink className="h-3 w-3" />
      </a>
    ) : <span className="text-[12px] text-gray-400">Not set</span>
  );

  return (
    <>
      <div className="sm:col-span-2 lg:col-span-3">
        <span className="text-gray-400">Location</span>
        {loading ? (
          <div className="text-[12px] text-gray-400">Loading…</div>
        ) : (
          <div className="mt-0.5 space-y-1.5">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <MapPin className="h-3.5 w-3.5 text-green-600 shrink-0" />
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 w-20">Check-in</span>
              <Coordinates point={checkin} />
              {checkin && (
                <span className="text-[11px] text-gray-400">recorded on site by GPS</span>
              )}
              {checkin && isAdmin() && (
                <button
                  onClick={() => reset('checkin')}
                  disabled={busy}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase rounded border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
                  title="Clear the GPS position recorded at check-in"
                >
                  <RotateCcw className="h-3 w-3" /> Reset
                </button>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <MapPin className="h-3.5 w-3.5 text-blue-600 shrink-0" />
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 w-20">Map pin</span>
              <Coordinates point={map} />
              {map?.source && (
                <span className="text-[11px] text-gray-400">{SOURCE_NOTE[map.source] || map.source}</span>
              )}
              <button
                onClick={() => setEditing(true)}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase rounded border border-blue-200 text-blue-600 hover:bg-blue-50"
                title="Set the map pin from a Google Maps link"
              >
                <Pencil className="h-3 w-3" /> {map ? 'Change' : 'Set'}
              </button>
              {map && isAdmin() && (
                <button
                  onClick={() => reset('map')}
                  disabled={busy}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase rounded border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
                  title="Clear the map pin"
                >
                  <RotateCcw className="h-3 w-3" /> Reset
                </button>
              )}
            </div>

            {!checkin && !map && (
              <p className="text-[11px] text-gray-400">
                No position at all — this customer sorts to the end of any route by distance.
              </p>
            )}
          </div>
        )}
      </div>

      {editing && (
        <VisitLocationModal
          customerId={customerId}
          customerName={customerName}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); load(); }}
        />
      )}
    </>
  );
};

export default CustomerLocationCard;
